import { Menu } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Link } from 'react-router'
import { Logo } from '#app/components/common/Logo'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { Input } from '#app/components/ui/input'
import { emitAnalyticsEvent, setSignupSource } from '#app/utils/pz/analytics'
import { useFeatureFlags } from '#app/utils/pz/featureFlags'
import { buildMarketingMenus } from '#app/utils/pz/navigation/menuBuilders'
import { path, publicRoutes } from '#app/utils/pz/routes'
import { useOptionalUser } from '#app/utils/user'
import { NavMenu, type MarketingNavCategory } from './navbar/nav-menu'
import { NavUserMenu } from './navbar/nav-user-menu'
import { NavigationSheet } from './navbar/navigation-sheet'

type NavbarProps = {
	navRoutes?: any[]
	navPaths?: any
	categories?: MarketingNavCategory[]
	signupHrefOverride?: string
}

const Navbar = ({
	navRoutes,
	navPaths,
	categories,
	signupHrefOverride,
}: NavbarProps) => {
	const { t } = useTranslation()
	const translate = useCallback(
		(key: string) => {
			const result = t(key as any)
			return typeof result === 'string' ? result : String(result)
		},
		[t],
	)
	const featureFlags = useFeatureFlags()
	const user = useOptionalUser()
	const ctx = useMemo(() => ({ featureFlags, user }), [featureFlags, user])
	const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)
	const { ctas, marketingLinks, userMenuItems } = useMemo(() => {
		const menus = buildMarketingMenus({
			t: translate,
			ctx,
			navRoutes,
			navPaths,
			signupHrefOverride,
		})

		return {
			ctas: menus.tools,
			marketingLinks: menus.marketing,
			userMenuItems: menus.user,
		}
	}, [ctx, navPaths, navRoutes, signupHrefOverride, translate])

	const signupCtaHref =
		ctas.find((item) => item.href.startsWith(path('auth.signup')))?.href ??
		path('auth.signup')
	const signInCtaHref =
		ctas.find((item) => item.href.startsWith(path('auth.login')))?.href ??
		path('auth.login')

	const handleSignupClick = () => {
		setSignupSource('hero_cta')
		emitAnalyticsEvent({
			name: 'signup_started',
			properties: { source: 'hero_cta' },
		})
	}

	return (
		<nav className="bg-background relative h-16 border-b">
			{isMobileSearchOpen ? (
				<div className="bg-background absolute inset-x-0 top-0 z-50 h-16 border-b md:hidden">
					<div className="mx-auto flex h-full max-w-screen-xl items-center gap-3 px-4 sm:px-6">
						<Form
							role="search"
							method="get"
							action={publicRoutes.courses.list()}
							className="relative flex-1"
							onSubmit={() => setIsMobileSearchOpen(false)}
						>
							<Input
								autoFocus
								type="search"
								name="q"
								placeholder={t('marketing.nav.searchPlaceholder')}
								className="pl-9"
								autoComplete="off"
							/>
							<div className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
								<Icon name="magnifying-glass" />
							</div>
						</Form>
						<Button
							variant="ghost"
							onClick={() => setIsMobileSearchOpen(false)}
						>
							{t('common.cancel')}
						</Button>
					</div>
				</div>
			) : null}
			<div className="mx-auto flex h-full max-w-screen-xl items-center px-4 sm:px-6 lg:px-8">
				{/* Mobile Header */}
				<div className="flex w-full items-center md:hidden">
					<div className="flex flex-1 items-center">
						<NavigationSheet
							categories={categories}
							marketingLinks={marketingLinks}
							signupHref={signupCtaHref}
							signInHref={signInCtaHref}
							trigger={
								<Button
									variant="outline"
									size="icon"
									aria-label={t('common.toggle')}
								>
									<Menu />
								</Button>
							}
						/>
					</div>
					<div className="flex flex-1 justify-center">
						<Link
							to={path('home')}
							aria-label={t('navigation.breadcrumb.home')}
						>
							<Logo imgClassName="w-28" />
						</Link>
					</div>
					<div className="flex flex-1 items-center justify-end gap-2">
						<Button
							variant="outline"
							size="icon"
							onClick={() => setIsMobileSearchOpen(true)}
							aria-label={t('common.actions.search')}
						>
							<Icon name="magnifying-glass" />
						</Button>
						{user ? <NavUserMenu items={userMenuItems} user={user} /> : null}
					</div>
				</div>

				{/* Desktop Header */}
				<div className="hidden w-full items-center justify-between md:flex">
					<Link to={path('home')} aria-label={t('navigation.breadcrumb.home')}>
						<Logo />
					</Link>

					<div className="flex items-center gap-6">
						{/* Desktop Menu */}
						<NavMenu
							className="hidden md:block"
							categories={categories}
							marketingLinks={marketingLinks}
						/>
						{/* Desktop Search */}
						<Form
							role="search"
							method="get"
							action={publicRoutes.courses.list()}
							className="relative hidden w-[320px] md:block"
						>
							<Input
								type="search"
								name="q"
								placeholder={t('marketing.nav.searchPlaceholder')}
								className="pl-9"
								autoComplete="off"
							/>
							<div className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
								<Icon name="magnifying-glass" />
							</div>
						</Form>
						{/* Show user menu when logged in */}
						{user ? (
							<NavUserMenu items={userMenuItems} user={user} />
						) : ctas.length > 0 ? (
							// Show CTAs when not logged in and CTAs exist
							<>
								{ctas.map((item, idx) => {
									const isOutline = idx === 0 && ctas.length > 1
									const className = idx === 0 ? 'hidden sm:inline-flex' : ''
									const content = <span>{item.name}</span>
									return (
										<Button
											key={item.id}
											variant={isOutline ? 'outline' : 'default'}
											className={className}
											asChild
										>
											{item.target ||
											item.href.startsWith('http') ||
											item.href.startsWith('mailto:') ? (
												<a
													href={item.href}
													target={item.target}
													rel={
														item.target === '_blank' ? 'noreferrer' : undefined
													}
												>
													{content}
												</a>
											) : (
												<Link to={item.href}>{content}</Link>
											)}
										</Button>
									)
								})}
							</>
						) : (
							// Show login/signup buttons when not logged in and no CTAs
							<>
								<Button
									variant="outline"
									className="hidden sm:inline-flex"
									asChild
								>
									<Link to={signInCtaHref}>{t('marketing.nav.signIn')}</Link>
								</Button>
								<Button asChild>
									<Link to={signupCtaHref} onClick={handleSignupClick}>
										{t('marketing.nav.getStarted')}
									</Link>
								</Button>
							</>
						)}
					</div>
				</div>
			</div>
		</nav>
	)
}

export default Navbar
